import type { Drill, Hint } from "./types.ts";
import { HARNESS_KIT } from "./harness-kit.ts";

const starter = `type Slot = [string, string];   // ["09:00", "10:30"]

/**
 * When are both people free for at least \`durationMinutes\`, inside the
 * working window? Earliest first.
 */
function availableSlots(
  interviewerBusy: Slot[],
  candidateBusy: Slot[],
  workingHours: Slot,
  durationMinutes: number,
): Slot[] {
  // TODO
  return [];
}
`;

const solution = `type Slot = [string, string];

const toMinutes = (t: string): number => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));

const toClock = (m: number): string =>
  String(Math.floor(m / 60)).padStart(2, "0") + ":" + String(m % 60).padStart(2, "0");

function availableSlots(
  interviewerBusy: Slot[],
  candidateBusy: Slot[],
  workingHours: Slot,
  durationMinutes: number,
): Slot[] {
  const open = toMinutes(workingHours[0]);
  const close = toMinutes(workingHours[1]);

  // Both diaries are the same obstacle: neither person's meeting cares whose it
  // was. Clip to the working window first so anything outside it stops mattering,
  // then sort, because the sweep below is only correct on ordered input.
  const busy = [...interviewerBusy, ...candidateBusy]
    .map(([s, e]): [number, number] => [Math.max(open, toMinutes(s)), Math.min(close, toMinutes(e))])
    .filter(([s, e]) => e > s)
    .sort((a, b) => a[0] - b[0]);

  const free: [number, number][] = [];
  let cursor = open;

  // The cursor is the end of the busiest block seen so far. Overlapping and
  // back-to-back meetings collapse into it on their own, so there is no separate
  // merge step to get wrong.
  for (const [start, end] of busy) {
    if (start > cursor) free.push([cursor, start]);
    cursor = Math.max(cursor, end);
  }
  if (cursor < close) free.push([cursor, close]);

  return free
    .filter(([s, e]) => e - s >= durationMinutes)
    .map(([s, e]): Slot => [toClock(s), toClock(e)]);
}`;

const harness = HARNESS_KIT + `
if (typeof availableSlots !== "function") {
  throw new Error("availableSlots is not defined. Keep the function name as given.");
}

var __DAY = ["09:00", "17:00"];

function __slots(a, b, hours, mins) {
  return function () { return availableSlots(a, b, hours, mins); };
}

const __tests = [
  {
    name: "returns the gaps both people share",
    about: "The basic contract: subtract both diaries from the working day.",
    run: function () {
      __expectExact("the worked example",
        __slots([["09:00", "10:30"], ["12:00", "13:00"]], [["10:30", "12:00"], ["15:00", "16:00"]], __DAY, 45),
        [["13:00", "15:00"], ["16:00", "17:00"]]);
    }
  },
  {
    name: "an empty diary is the whole working day",
    about: "Nobody busy means one slot, the window itself, not an empty answer.",
    run: function () {
      __expectExact("nothing booked", __slots([], [], __DAY, 60), [["09:00", "17:00"]]);
    }
  },
  {
    name: "gaps shorter than the interview are dropped",
    about: "A 15 minute hole is not a slot for a 60 minute interview. Returning it wastes the scheduler's time and the candidate's.",
    run: function () {
      __expectExact("a 15 minute hole, 60 minutes needed",
        __slots([["09:00", "12:00"], ["12:15", "17:00"]], [], __DAY, 60), []);
    }
  },
  {
    name: "input arrives unsorted",
    hidden: true,
    about: "Calendar APIs do not promise an order, and a sweep over unsorted intervals invents slots that are already booked.",
    run: function () {
      __expectExact("afternoon listed before morning",
        __slots([["14:00", "17:00"], ["09:00", "12:00"]], [], __DAY, 60),
        [["12:00", "14:00"]]);
    }
  },
  {
    name: "overlapping meetings collapse",
    hidden: true,
    about: "Two people double-booked over the same hour is one obstacle, not two. Treating each separately produces a negative-length gap between them.",
    run: function () {
      __expectExact("09:00-12:00 overlapping 10:00-13:00",
        __slots([["09:00", "12:00"]], [["10:00", "13:00"]], __DAY, 60),
        [["13:00", "17:00"]]);
    }
  },
  {
    name: "a meeting entirely inside another changes nothing",
    hidden: true,
    about: "The containing block already ends later. Advancing the cursor to the shorter meeting's end re-opens time that is booked.",
    run: function () {
      __expectExact("10:00-16:00 containing 11:00-12:00",
        __slots([["10:00", "16:00"]], [["11:00", "12:00"]], __DAY, 30),
        [["09:00", "10:00"], ["16:00", "17:00"]]);
    }
  },
  {
    name: "back-to-back meetings leave no gap",
    hidden: true,
    about: "One meeting ending exactly when the next begins is not a zero-minute opening. Emitting it puts empty slots in front of a recruiter.",
    run: function () {
      __expectExact("10:00-11:00 then 11:00-12:00",
        __slots([["10:00", "11:00"], ["11:00", "12:00"]], [], __DAY, 30),
        [["09:00", "10:00"], ["12:00", "17:00"]]);
    }
  },
  {
    name: "meetings outside the working window are clipped",
    hidden: true,
    about: "An 08:00 stand-up must not shorten the 09:00 slot, and a 19:00 dinner must not appear as a gap in the middle of the day.",
    run: function () {
      __expectExact("08:00-09:30 and 18:00-19:00 against a 09:00-17:00 day",
        __slots([["08:00", "09:30"], ["18:00", "19:00"]], [], __DAY, 30),
        [["09:30", "17:00"]]);
    }
  },
  {
    name: "a gap exactly as long as the interview counts",
    hidden: true,
    about: "The comparison is at least, not more than. An off-by-one here quietly throws away the only slot on a busy day.",
    run: function () {
      __expectExact("a 60 minute gap, 60 minutes needed",
        __slots([["09:00", "13:00"], ["14:00", "17:00"]], [], __DAY, 60),
        [["13:00", "14:00"]]);
    }
  },
  {
    name: "a fully booked day returns nothing",
    hidden: true,
    about: "No availability is an empty list, not the working window with nothing subtracted.",
    run: function () {
      __expectExact("booked 09:00-17:00", __slots([["09:00", "17:00"]], [], __DAY, 15), []);
    }
  },
  {
    name: "returns whole gaps, not duration-sized chunks",
    hidden: true,
    about: "The caller decides where inside a free block to put the interview. Slicing a three hour gap into six half-hour slots is a different, longer answer.",
    run: function () {
      __expectExact("a three hour gap, 30 minutes needed",
        __slots([["09:00", "10:00"], ["13:00", "17:00"]], [], __DAY, 30),
        [["10:00", "13:00"]]);
    }
  },
  {
    name: "the window can be any pair of times",
    hidden: true,
    about: "Nine to five is a default, not a rule. Hard-coding it breaks the first time somebody schedules across a different timezone's overlap.",
    run: function () {
      __expectExact("a 13:00-15:30 window",
        __slots([["13:30", "14:00"]], [["09:00", "13:15"]], ["13:00", "15:30"], 45),
        [["14:00", "15:30"]]);
    }
  }
];
`;

const hints: Hint[] = [
  {
    label: "Nudge",
    body: "You are handed two diaries. Ask whether whose meeting it is ever changes the answer. It does not, so the first move is to stop treating them as two things.",
    code: `const toMinutes = (t: string): number => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));

const open = toMinutes(workingHours[0]);
const close = toMinutes(workingHours[1]);

// One list of obstacles, clipped to the window, then sorted. The sweep below is
// only correct on ordered input, and a calendar API promises no order.
const busy = [...interviewerBusy, ...candidateBusy]
  .map(([s, e]): [number, number] => [Math.max(open, toMinutes(s)), Math.min(close, toMinutes(e))])
  .filter(([s, e]) => e > s)
  .sort((a, b) => a[0] - b[0]);`,
  },
  {
    label: "Approach",
    body: "Sweep with a cursor holding the latest end seen so far. A gap exists whenever the next block starts after the cursor. The Math.max is what makes overlaps and one meeting sitting inside another collapse on their own, so there is no separate merge step to get wrong.",
    code: `const free: [number, number][] = [];
let cursor = open;

for (const [start, end] of busy) {
  if (start > cursor) free.push([cursor, start]);   // strictly after: back-to-back leaves no gap
  cursor = Math.max(cursor, end);                   // a contained meeting cannot pull the cursor back
}
if (cursor < close) free.push([cursor, close]);

return free
  .filter(([s, e]) => e - s >= durationMinutes)     // at least, not more than
  .map(([s, e]): Slot => [toClock(s), toClock(e)]);`,
  },
  {
    label: "The trap",
    body: "What breaks implementations here is the boundaries. A meeting ending exactly when the next begins is not a zero length gap, so the comparison is strict. A gap exactly as long as the interview does count, so that comparison is not. And you return whole free blocks, not duration-sized chunks: where inside the block to put the interview is the caller's decision.",
  },
];

export const interviewScheduler: Drill = {
  id: "interview-scheduler",
  track: "typescript",
  title: "Fit an interview into two diaries",
  pattern: "interval-sweep",
  difficulty: "hard",
  basePoints: 300,
  timeBudgetMs: 30 * 60 * 1000,
  statement: `A scheduler is given the interviewer's diary and the candidate's diary and has to offer the times both are free.

\`\`\`ts
type Slot = [string, string];   // ["09:00", "10:30"]

function availableSlots(
  interviewerBusy: Slot[],
  candidateBusy: Slot[],
  workingHours: Slot,
  durationMinutes: number,
): Slot[];
\`\`\`

## Requirements

- **Both diaries are obstacles.** The answer is the working window minus everything in either one.
- **Input arrives unsorted**, and blocks may overlap or contain one another.
- **Back-to-back meetings leave no gap.** A meeting ending at 11:00 and the next starting at 11:00 is one solid block.
- **Anything outside the working window is clipped**, not counted.
- **Only gaps of at least \`durationMinutes\` are returned**, whole, earliest first.

## Example

\`\`\`ts
availableSlots(
  [["09:00", "10:30"], ["12:00", "13:00"]],
  [["10:30", "12:00"], ["15:00", "16:00"]],
  ["09:00", "17:00"],
  45,
);
// [["13:00", "15:00"], ["16:00", "17:00"]]
\`\`\`

## Where this gets interesting

Two diaries sound like two problems, and the answer gets much shorter the moment you stop treating them as separate. Nobody's free time cares whose meeting blocked it, so concatenate, sort once, and sweep with a cursor holding the latest end seen so far. Overlaps and containment then fall out of \`Math.max\` rather than needing a merge pass of their own.

The cases that break implementations are all boundaries: the meeting that ends exactly when the next begins, the gap exactly as long as the interview, and the 08:00 stand-up that has no business shortening a 09:00 slot. Decide what each of those means before you write the loop, because you cannot see them in the happy-path example.`,
  hints,
  starter,
  harness,
  solution,
  complexity: "O(n log n) for the sort over both diaries, then one linear sweep. Space is O(n) for the merged list.",
};
